import React, { useEffect, useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../ui/select";
import { api } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { cn } from "../../lib/utils";
import { Building2, AlertTriangle, Siren, FileWarning, Loader2 } from "lucide-react";

const CATEGORIES = [
  { key: "vendor-requests", label: "Vendor Requests", icon: Building2, statuses: ["pending", "approved", "rejected"] },
  { key: "risk-submissions", label: "Risk Submissions", icon: AlertTriangle, statuses: ["pending", "triaged", "dismissed"] },
  { key: "incidents", label: "Incidents", icon: Siren, statuses: ["open", "investigating", "resolved"] },
  { key: "policy-exceptions", label: "Exceptions", icon: FileWarning, statuses: ["pending", "approved", "denied"] },
];

const STATUS_CLS = {
  pending: "text-amber-600 border-amber-500/40 bg-amber-500/10",
  open: "text-amber-600 border-amber-500/40 bg-amber-500/10",
  triaged: "text-sky-600 border-sky-500/40 bg-sky-500/10",
  investigating: "text-sky-600 border-sky-500/40 bg-sky-500/10",
  approved: "text-emerald-600 border-emerald-500/40 bg-emerald-500/10",
  resolved: "text-emerald-600 border-emerald-500/40 bg-emerald-500/10",
  rejected: "text-red-600 border-red-500/40 bg-red-500/10",
  denied: "text-red-600 border-red-500/40 bg-red-500/10",
  dismissed: "text-muted-foreground border-border bg-muted/30",
};

function describeItem(category, item) {
  switch (category) {
    case "vendor-requests": return { title: item.tool_name, body: item.reason, who: item.requester_name, email: item.requester_email };
    case "risk-submissions": return { title: item.title, body: `${item.category} — ${item.description}`, who: item.submitter_name, email: item.submitter_email };
    case "incidents": return { title: item.title, body: `${item.severity} severity — ${item.description}`, who: item.reporter_name, email: item.reporter_email };
    case "policy-exceptions": return { title: item.policy_title, body: item.reason, who: item.requester_name, email: item.requester_email };
    default: return { title: "", body: "" };
  }
}

function TriageRow({ category, item, statuses, onSaved }) {
  const { toast } = useToast();
  const [status, setStatus] = useState(item.status);
  const [note, setNote] = useState(item.admin_note || "");
  const [saving, setSaving] = useState(false);
  const { title, body, who, email } = describeItem(category, item);
  const dirty = status !== item.status || note !== (item.admin_note || "");

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/employee-portal/admin/${category}/${item.id}`, { status, admin_note: note || null });
      toast({ title: "Updated" });
      onSaved?.();
    } catch (e) {
      toast({ title: "Couldn't save", description: e?.response?.data?.detail || "", variant: "destructive" });
    } finally { setSaving(false); }
  };

  return (
    <Card data-testid={`admin-triage-row-${item.id}`}>
      <CardContent className="p-4 space-y-2.5">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[13px] font-medium">{title}</div>
            <div className="text-[12px] text-muted-foreground mt-0.5">{body}</div>
            <div className="text-[11px] text-muted-foreground mt-1">{who} &lt;{email}&gt; · {new Date(item.created_at).toLocaleDateString()}</div>
          </div>
          <Badge variant="outline" className={cn("flex-shrink-0 capitalize", STATUS_CLS[item.status])}>{item.status}</Badge>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 h-8 text-[12px]" data-testid={`admin-triage-status-select-${item.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
          </Select>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Admin note (optional)" className="flex-1 min-w-[160px] h-8 text-[12px]" data-testid={`admin-triage-note-input-${item.id}`} />
          <Button size="sm" disabled={!dirty || saving} onClick={save} className="gap-1.5 h-8" data-testid={`admin-triage-save-btn-${item.id}`}>
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Save
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default function PortalAdminReview() {
  const [active, setActive] = useState("vendor-requests");
  const [items, setItems] = useState(null);

  const activeCfg = CATEGORIES.find((c) => c.key === active);

  const load = () => api.get(`/employee-portal/admin/${active}`).then((r) => setItems(r.data.items));
  useEffect(() => { setItems(null); load(); }, [active]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="portal-admin-review">
      <h1 className="text-xl font-semibold">Admin Review</h1>
      <div className="flex gap-1.5 flex-wrap">
        {CATEGORIES.map((c) => (
          <button
            key={c.key}
            type="button"
            onClick={() => setActive(c.key)}
            className={cn(
              "inline-flex items-center gap-1.5 px-3 h-9 rounded-lg text-[12.5px] font-medium border transition-colors",
              active === c.key ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground",
            )}
            data-testid={`admin-review-category-${c.key}`}
          >
            <c.icon className="w-3.5 h-3.5" /> {c.label}
          </button>
        ))}
      </div>
      {!items ? (
        <div className="h-40 rounded-xl bg-muted/40 animate-pulse" />
      ) : (
        <div className="space-y-2">
          {items.map((item) => (
            <TriageRow key={item.id} category={active} item={item} statuses={activeCfg.statuses} onSaved={load} />
          ))}
          {items.length === 0 && <p className="text-[13px] text-muted-foreground">Nothing submitted in this category yet.</p>}
        </div>
      )}
    </div>
  );
}
